import client from "./client";

// ────────── Enums ──────────

export type AssetStatus =
  | "AVAILABLE"
  | "ASSIGNED"
  | "IN_REPAIR"
  | "RETIRED"
  | "LOST";

export type AssetType =
  | "LAPTOP"
  | "DESKTOP"
  | "MONITOR"
  | "PHONE"
  | "TABLET"
  | "ACCESSORY"
  | "FURNITURE"
  | "SOFTWARE_LICENSE"
  | "OTHER";

export type AssetCondition = "NEW" | "GOOD" | "FAIR" | "POOR" | "BROKEN";

// ────────── Entity Types ──────────

export interface AssetAssignment {
  id: string;
  org_id: string;
  asset_id: string;
  employee_id: string;
  assigned_at: string;
  expected_return_at: string | null;
  returned_at: string | null;
  condition_on_assign: AssetCondition | null;
  condition_on_return: AssetCondition | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Asset {
  id: string;
  org_id: string;
  asset_tag: string;
  asset_type: AssetType;
  status: AssetStatus;
  model: string | null;
  manufacturer: string | null;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_cost: number | null;
  currency: string | null;
  warranty_expires_on: string | null;
  current_location: string | null;
  notes: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  /** Current active assignment, if any (backend includes this on list/get). */
  active_assignment: AssetAssignment | null;
}

export interface AssetCreate {
  asset_tag: string;
  asset_type: AssetType;
  status?: AssetStatus;
  // NOTE: ``condition`` lives on AssetAssignment (on-assign / on-return),
  // not on Asset itself. The backend silently drops this field — keeping
  // it here only so old callers don't break at compile time; prefer
  // setting it via the assignment endpoint.
  condition?: AssetCondition;
  model?: string | null;
  manufacturer?: string | null;
  serial_number?: string | null;
  purchase_date?: string | null;
  // Backend field is ``purchase_cost`` (Numeric(12,2)); keep the TS name
  // aligned so pydantic doesn't silently drop the value.
  purchase_cost?: number | null;
  currency?: string | null;
  notes?: string | null;
}

export interface AssetAssignPayload {
  employee_id: string;
  expected_return_at?: string | null;
  condition?: AssetCondition;
  notes?: string | null;
}

export interface AssetReturnPayload {
  condition_on_return?: AssetCondition;
  notes?: string | null;
}

// ────────── Assets ──────────

export async function listAssets(params?: {
  status?: AssetStatus;
  asset_type?: AssetType;
  assigned_to?: string | "me";
  include_archived?: boolean;
}): Promise<Asset[]> {
  // NOTE: backend is ``@router.post("")`` with prefix ``/assets`` and
  // ``redirect_slashes=False`` on the FastAPI app — trailing slashes 404.
  const { data } = await client.get<Asset[]>("/assets", { params });
  return data;
}

export async function createAsset(payload: AssetCreate): Promise<Asset> {
  const { data } = await client.post<Asset>("/assets", payload);
  return data;
}

export async function getAsset(id: string): Promise<Asset> {
  const { data } = await client.get<Asset>(`/assets/${id}`);
  return data;
}

export async function updateAsset(
  id: string,
  payload: Partial<AssetCreate>,
): Promise<Asset> {
  const { data } = await client.patch<Asset>(`/assets/${id}`, payload);
  return data;
}

export async function archiveAsset(id: string): Promise<void> {
  await client.delete(`/assets/${id}`);
}

export async function assignAsset(
  id: string,
  payload: AssetAssignPayload,
): Promise<Asset> {
  const { data } = await client.post<Asset>(`/assets/${id}/assign`, payload);
  return data;
}

export async function returnAsset(
  id: string,
  payload: AssetReturnPayload = {},
): Promise<Asset> {
  const { data } = await client.post<Asset>(`/assets/${id}/return`, payload);
  return data;
}

// ────────── Assignments ──────────

export async function listAssetAssignments(): Promise<AssetAssignment[]> {
  const { data } = await client.get<AssetAssignment[]>("/assets/assignments");
  return data;
}

export async function getAssetAssignment(
  id: string,
): Promise<AssetAssignment> {
  const { data } = await client.get<AssetAssignment>(
    `/assets/assignments/${id}`,
  );
  return data;
}
